import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Filter, Eye, Calendar, User, Folder, Ticket, AlertCircle, Clock, CheckCircle, XCircle, LayoutGrid, List } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import TaskPeekOverview from './TaskPeekOverview';

const API = `${process.env.REACT_APP_BACKEND_URL}/api` || 'http://localhost:8001/api';

const UnifiedTasks = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [teams, setTeams] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTasks, setTotalTasks] = useState(0);
  const [itemsPerPage] = useState(50);
  const [viewMode, setViewMode] = useState(() => {
    // Restore view mode from localStorage
    return localStorage.getItem('tasksViewMode') || 'list';
  });
  const [selectedTask, setSelectedTask] = useState(null);
  
  // Get current user from localStorage
  const [currentUser, setCurrentUser] = useState(null);

  // Helper function to truncate text with hover tooltip
  const TruncatedText = ({ text, maxLength = 15, className = "" }) => {
    if (!text) return <span className={className}>-</span>;
    
    const shouldTruncate = text.length > maxLength;
    const displayText = shouldTruncate ? `${text.substring(0, maxLength)}...` : text;
    
    return (
      <span 
        className={`${className} ${shouldTruncate ? 'cursor-help' : ''}`}
        title={shouldTruncate ? text : undefined}
      >
        {displayText}
      </span>
    );
  };

  useEffect(() => {
    // Get current user from localStorage
    const userData = localStorage.getItem('bizmanager_user');
    if (userData) {
      const user = JSON.parse(userData);
      setCurrentUser(user);
      
      // If user is an employee, auto-set team filter to their team
      if (user.isEmployee && user.team_type_id) {
        setTeamFilter(user.team_type_id);
      }
    }
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchTasks();
      fetchTeams();
    }
  }, [typeFilter, statusFilter, teamFilter, currentUser, currentPage]);

  // Reset to first page when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [typeFilter, statusFilter, teamFilter]);

  // Save view mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('tasksViewMode', viewMode);
  }, [viewMode]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (typeFilter) params.append('task_type', typeFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (teamFilter) params.append('team_id', teamFilter);
      
      // Add pagination parameters
      const skip = (currentPage - 1) * itemsPerPage;
      params.append('skip', skip.toString());
      params.append('limit', itemsPerPage.toString());
      
      const response = await axios.get(`${API}/tasks/unified?${params.toString()}`);
      setTasks(response.data);
      
      // For now, we'll get total count by making a separate request without pagination
      // In a real app, the API should return total count with paginated results
      const totalResponse = await axios.get(`${API}/tasks/unified?${new URLSearchParams({
        ...(typeFilter && { task_type: typeFilter }),
        ...(statusFilter && { status: statusFilter }),
        ...(teamFilter && { team_id: teamFilter }),
        limit: '1000' // Get a large number to count total
      }).toString()}`);
      setTotalTasks(totalResponse.data.length);
    } catch (error) {
      console.error('Error fetching unified tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/team-types`);
      setTeams(response.data);
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const getTaskTypeIcon = (type) => {
    switch (type) {
      case 'Project':
        return <Folder className="h-4 w-4 text-blue-500" />;
      case 'Support':
        return <Ticket className="h-4 w-4 text-orange-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getTaskTypeBadgeClass = (type) => {
    switch (type) {
      case 'Project':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Support':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Open':
      case 'To Do':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'In Progress':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'In testing':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'Resolved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'Published (Prod)':
        return <CheckCircle className="h-4 w-4 text-purple-500" />;
      case 'Closed':
        return <XCircle className="h-4 w-4 text-gray-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Open':
      case 'To Do':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'In testing':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Resolved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Published (Prod)':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Closed':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleTaskView = (task) => {
    // Open peek overview instead of navigating
    setSelectedTask(task);
  };

  const handleClosePeekOverview = () => {
    setSelectedTask(null);
  };

  const handleNavigateFromPeek = (path) => {
    navigate(path);
  };

  const filteredTasks = tasks.filter(task =>
    task.task_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (task.team_name && task.team_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (task.assigned_employee_name && task.assigned_employee_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (task.project_name && task.project_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (task.client_name && task.client_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">All Tasks</h1>
          <p className="text-gray-600 mt-1">Unified view of project tasks and support tickets</p>
        </div>
        
        {/* View Mode Toggle */}
        <div className="flex items-center bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center px-4 py-2 rounded-md transition-colors ${
              viewMode === 'list'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <List className="w-4 h-4 mr-2" />
            List
          </button>
          <button
            onClick={() => setViewMode('kanban')}
            className={`flex items-center px-4 py-2 rounded-md transition-colors ${
              viewMode === 'kanban'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <LayoutGrid className="w-4 h-4 mr-2" />
            Kanban
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Types</option>
              <option value="Project">Project</option>
              <option value="Support">Support</option>
            </select>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Status</option>
              <option value="To Do">To Do</option>
              <option value="Open">Open</option>
              <option value="In Progress">In Progress</option>
              <option value="In testing">In testing</option>
              <option value="Resolved">Resolved</option>
              <option value="Published (Prod)">Published (Prod)</option>
              <option value="Closed">Closed</option>
            </select>

            <select
              value={teamFilter}
              onChange={(e) => setTeamFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Teams</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Conditional Rendering: Kanban or List View */}
      {viewMode === 'kanban' ? (
        <KanbanBoard 
          tasks={filteredTasks} 
          loading={loading} 
          onTaskView={handleTaskView}
          onTaskUpdate={setTasks}
          allTasks={tasks}
        />
      ) : (
        /* Tasks Table */
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-500 mt-2">Loading tasks...</p>
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="p-8 text-center">
              <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks found</h3>
              <p className="text-gray-500">
                {searchTerm || typeFilter || statusFilter || teamFilter
                  ? "No tasks match your current filters."
                  : "No tasks available at the moment."}
              </p>
            </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Task Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Team
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Person
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTasks.map((task) => (
                  <tr key={task.id} className="hover:bg-gray-50">
                    {/* Type Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getTaskTypeIcon(task.type)}
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getTaskTypeBadgeClass(task.type)}`}>
                          {task.type}
                        </span>
                      </div>
                    </td>
                    
                    {/* ID Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono font-medium text-blue-600">
                        {task.type === 'Support' ? 
                          `#${task.ticket_id?.slice(-8) || task.id.slice(-8)}` : 
                          `T${task.task_index + 1}`
                        }
                      </div>
                    </td>
                    
                    {/* Task Name Column - Clean, no project/client info */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <TruncatedText 
                        text={task.task_name} 
                        maxLength={20}
                        className="font-medium text-gray-900"
                      />
                    </td>
                    
                    {/* Client Column - New column with truncation */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <TruncatedText 
                        text={task.client_name} 
                        maxLength={12}
                        className="text-gray-900"
                      />
                    </td>
                    
                    {/* Team Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <TruncatedText 
                        text={task.team_name} 
                        maxLength={10}
                        className="text-gray-900"
                      />
                    </td>
                    
                    {/* Person Column - With truncation */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {task.assigned_employee_name ? (
                          <>
                            <div className="w-6 h-6 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-medium">
                              {task.assigned_employee_name.charAt(0)}
                            </div>
                            <TruncatedText 
                              text={task.assigned_employee_name} 
                              maxLength={10}
                              className="text-gray-900"
                            />
                          </>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <div className="w-6 h-6 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-xs font-medium">
                              ?
                            </div>
                            <span className="text-orange-600 text-sm">
                              {task.type === 'Support' ? 'Awaiting Assignment' : 'Unassigned'}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    
                    {/* Status Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(task.status)}
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusBadgeClass(task.status)}`}>
                          {task.status}
                        </span>
                      </div>
                    </td>
                    
                    {/* Created Column */}
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(task.created_at).toLocaleDateString()}
                    </td>
                    
                    {/* Action Column */}
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleTaskView(task)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination - Only in List View */}
        {filteredTasks.length > 0 && (
          <div className="mt-6 flex justify-between items-center px-6 pb-4">
            <div className="text-sm text-gray-500">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalTasks)} of {totalTasks} tasks
            </div>
          
          {Math.ceil(totalTasks / itemsPerPage) > 1 && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, Math.ceil(totalTasks / itemsPerPage)) }, (_, i) => {
                  const totalPages = Math.ceil(totalTasks / itemsPerPage);
                  let pageNumber;
                  
                  if (totalPages <= 5) {
                    pageNumber = i + 1;
                  } else if (currentPage <= 3) {
                    pageNumber = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNumber = totalPages - 4 + i;
                  } else {
                    pageNumber = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setCurrentPage(pageNumber)}
                      className={`px-3 py-1 text-sm border rounded-md ${
                        currentPage === pageNumber
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(totalTasks / itemsPerPage)))}
                disabled={currentPage === Math.ceil(totalTasks / itemsPerPage)}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
          </div>
        )}
        </div>
      )}

      {/* Task Peek Overview */}
      {selectedTask && (
        <TaskPeekOverview
          task={selectedTask}
          onClose={handleClosePeekOverview}
          onNavigate={handleNavigateFromPeek}
        />
      )}
    </div>
  );
};

// Kanban Board Component
const KanbanBoard = ({ tasks, loading, onTaskView, onTaskUpdate, allTasks }) => {
  const [draggedTask, setDraggedTask] = React.useState(null);
  const [draggedOverColumn, setDraggedOverColumn] = React.useState(null);

  const statuses = [
    { name: 'To Do', color: 'bg-gray-100', textColor: 'text-gray-700', borderColor: 'border-gray-300' },
    { name: 'Open', color: 'bg-blue-100', textColor: 'text-blue-700', borderColor: 'border-blue-300' },
    { name: 'In Progress', color: 'bg-yellow-100', textColor: 'text-yellow-700', borderColor: 'border-yellow-300' },
    { name: 'In testing', color: 'bg-purple-100', textColor: 'text-purple-700', borderColor: 'border-purple-300' },
    { name: 'Resolved', color: 'bg-green-100', textColor: 'text-green-700', borderColor: 'border-green-300' },
    { name: 'Published (Prod)', color: 'bg-teal-100', textColor: 'text-teal-700', borderColor: 'border-teal-300' },
    { name: 'Closed', color: 'bg-gray-200', textColor: 'text-gray-600', borderColor: 'border-gray-400' }
  ];

  const getTasksByStatus = (status) => {
    return tasks.filter(task => task.status === status);
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
      case 'high':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'low':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.innerHTML);
  };

  const handleDragOver = (e, status) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDraggedOverColumn(status);
  };

  const handleDragLeave = (e) => {
    setDraggedOverColumn(null);
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    setDraggedOverColumn(null);

    if (!draggedTask || draggedTask.status === newStatus) {
      setDraggedTask(null);
      return;
    }

    // Optimistically update the UI
    const previousTasks = allTasks;
    const updatedTasks = allTasks.map(task => {
      if (task.id === draggedTask.id) {
        return { ...task, status: newStatus };
      }
      return task;
    });
    onTaskUpdate(updatedTasks);

    // Update task status via API
    try {
      if (draggedTask.type === 'Support') {
        // For support tickets, use ticket_id field
        const ticketId = draggedTask.ticket_id;
        if (!ticketId) {
          // Revert on error
          onTaskUpdate(previousTasks);
          alert('Invalid ticket ID');
          setDraggedTask(null);
          return;
        }
        await axios.patch(`${API}/support-tickets/${ticketId}`, { status: newStatus });
      } else {
        // For task assignments, use project_id and task_index
        const projectId = draggedTask.project_id;
        const taskIndex = draggedTask.task_index;
        
        if (!projectId || taskIndex === null || taskIndex === undefined) {
          // Revert on error
          onTaskUpdate(previousTasks);
          alert('Invalid task data');
          setDraggedTask(null);
          return;
        }
        
        // Construct composite ID: project_id_task_index_employee_id
        const employeeId = draggedTask.assigned_employee_id || 'unassigned';
        const assignmentId = `${projectId}_${taskIndex}_${employeeId}`;
        
        await axios.patch(`${API}/task-assignments/${assignmentId}`, { status: newStatus });
      }
      
      // Success - keep the updated state
      toast.success('Task status updated successfully');
      
    } catch (error) {
      // Revert the optimistic update on error
      onTaskUpdate(previousTasks);
      console.error('Error updating task status:', error);
      console.error('Task data:', draggedTask);
      alert(`Failed to update task status: ${error.response?.data?.detail || error.message}`);
    }

    setDraggedTask(null);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-gray-500 ml-3">Loading tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex space-x-4 overflow-x-auto pb-4">
        {statuses.map((status) => {
          const statusTasks = getTasksByStatus(status.name);
          
          return (
            <div key={status.name} className="flex-shrink-0 w-80">
              {/* Column Header */}
              <div className={`${status.color} rounded-t-lg px-4 py-3 border-b-2 ${status.borderColor}`}>
                <div className="flex items-center justify-between">
                  <h3 className={`font-semibold ${status.textColor}`}>
                    {status.name}
                  </h3>
                  <span className={`text-sm font-medium px-2 py-1 rounded-full ${status.color} ${status.textColor}`}>
                    {statusTasks.length}
                  </span>
                </div>
              </div>

              {/* Column Content */}
              <div 
                className={`bg-white rounded-b-lg border border-t-0 border-gray-200 min-h-[500px] max-h-[600px] overflow-y-auto p-3 space-y-3 ${
                  draggedOverColumn === status.name ? 'ring-2 ring-blue-400 ring-opacity-50' : ''
                }`}
                onDragOver={(e) => handleDragOver(e, status.name)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, status.name)}
              >
                {statusTasks.length === 0 ? (
                  <div className="text-center py-8 text-gray-400 text-sm">
                    No tasks
                  </div>
                ) : (
                  statusTasks.map((task, index) => (
                    <div
                      key={index}
                      draggable
                      onDragStart={(e) => handleDragStart(e, task)}
                      onClick={() => onTaskView(task)}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-all cursor-move group relative"
                    >
                      {/* Header: Type Badge and Priority */}
                      <div className="flex items-center justify-between mb-3">
                        <span className={`text-xs px-3 py-1.5 rounded-full font-medium ${
                          task.type === 'Support'
                            ? 'bg-purple-100 text-purple-700 border border-purple-200'
                            : 'bg-blue-100 text-blue-700 border border-blue-200'
                        }`}>
                          {task.type === 'Support' ? (
                            <><Ticket className="inline w-3 h-3 mr-1" /> Support</>
                          ) : (
                            <><Folder className="inline w-3 h-3 mr-1" /> Project</>
                          )}
                        </span>
                        
                        {task.priority && (
                          <div className={`flex items-center text-xs px-2.5 py-1 rounded-full font-semibold ${getPriorityColor(task.priority)}`}>
                            {task.priority === 'urgent' || task.priority === 'high' ? '🔥' : ''}
                            {task.priority.toUpperCase()}
                          </div>
                        )}
                      </div>

                      {/* Task Title - More Prominent */}
                      <h4 className="font-semibold text-gray-900 text-base mb-3 line-clamp-2 leading-tight">
                        {task.task_name}
                      </h4>

                      {/* Due/End Date - Prominent Display */}
                      {(task.due_date || task.end_date) && (
                        (() => {
                          const dateToUse = task.type === 'Project' ? (task.end_date || task.due_date) : (task.due_date || task.end_date);
                          // Parse date as local date to avoid timezone issues
                          const [year, month, day] = dateToUse.split('-').map(Number);
                          const targetDate = new Date(year, month - 1, day); // month is 0-indexed
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          targetDate.setHours(0, 0, 0, 0);
                          
                          const daysRemaining = Math.ceil((targetDate - today) / (1000 * 60 * 60 * 24));
                          const isOverdue = daysRemaining < 0;
                          const isDueSoon = daysRemaining >= 0 && daysRemaining <= 3;
                          
                          return (
                            <div className={`flex items-center text-sm font-medium mb-3 px-3 py-2 rounded-lg ${
                              isOverdue
                                ? 'bg-red-50 text-red-700 border-2 border-red-300' 
                                : isDueSoon
                                ? 'bg-orange-50 text-orange-700 border-2 border-orange-300'
                                : 'bg-blue-50 text-blue-700 border border-blue-200'
                            }`}>
                              <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
                              <div className="flex-1">
                                <span className="block text-xs opacity-75">
                                  {task.type === 'Project' ? 'End Date' : 'Due Date'}
                                </span>
                                <span className="font-bold">
                                  {targetDate.toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric', 
                                    year: 'numeric' 
                                  })}
                                </span>
                                
                                {/* Days Remaining */}
                                {task.type === 'Project' && (
                                  <div className={`text-xs font-semibold mt-1 ${
                                    isOverdue ? 'text-red-800' : isDueSoon ? 'text-orange-800' : 'text-blue-800'
                                  }`}>
                                    {isOverdue ? (
                                      <span>Overdue by {Math.abs(daysRemaining)} day{Math.abs(daysRemaining) !== 1 ? 's' : ''}</span>
                                    ) : daysRemaining === 0 ? (
                                      <span>⏰ Due Today</span>
                                    ) : daysRemaining === 1 ? (
                                      <span>⏰ 1 day remaining</span>
                                    ) : (
                                      <span>{daysRemaining} days remaining</span>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              {isOverdue && (
                                <span className="text-xs font-bold bg-red-600 text-white px-2 py-0.5 rounded">OVERDUE</span>
                              )}
                              {!isOverdue && isDueSoon && task.type === 'Project' && (
                                <span className="text-xs font-bold bg-orange-600 text-white px-2 py-0.5 rounded">URGENT</span>
                              )}
                            </div>
                          );
                        })()
                      )}

                      {/* Divider */}
                      <div className="border-t border-gray-100 my-3"></div>

                      {/* Context Section */}
                      <div className="space-y-2 mb-3">
                        {task.project_name && (
                          <div className="flex items-center text-xs text-gray-600">
                            <Folder className="w-3.5 h-3.5 mr-2 flex-shrink-0 text-gray-400" />
                            <span className="truncate font-medium">{task.project_name}</span>
                          </div>
                        )}
                        
                        {task.client_name && (
                          <div className="flex items-center text-xs text-gray-600">
                            <User className="w-3.5 h-3.5 mr-2 flex-shrink-0 text-gray-400" />
                            <span className="truncate">{task.client_name}</span>
                          </div>
                        )}

                        {task.team_name && (
                          <div className="flex items-center text-xs text-gray-600">
                            <User className="w-3.5 h-3.5 mr-2 flex-shrink-0 text-gray-400" />
                            <span className="truncate">Team: {task.team_name}</span>
                          </div>
                        )}
                      </div>

                      {/* Assignee with Avatar */}
                      {task.assigned_employee_name && (
                        <div className="flex items-center mb-3 p-2 bg-gray-50 rounded-lg">
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center text-xs font-bold text-white mr-2 flex-shrink-0">
                            {task.assigned_employee_name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-xs text-gray-500 block">Assigned to</span>
                            <span className="text-sm font-medium text-gray-900 truncate block">
                              {task.assigned_employee_name}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Footer: Created Date */}
                      <div className="flex items-center justify-between text-xs text-gray-400 pt-2 border-t border-gray-100">
                        <div className="flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          Created {new Date(task.created_at).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </div>
                        
                        {/* Task ID if available */}
                        {task.id && (
                          <span className="font-mono text-xs opacity-50">
                            #{task.id.slice(-6)}
                          </span>
                        )}
                      </div>

                      {/* Hover effect indicator */}
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UnifiedTasks;